import { getUser, getVisitorInfo } from './../../../services'
import xss from 'xss'
import Analytics from '../../SiteNav/analytics.js'
import './utility.less'
import PersonalizedButton from './PersonalizedButton'
import Menubar from '../Navigation/menubar.js'

function IsLargeViewport() {
  return (
    (window.innerWidth * 16) /
      parseFloat(
        getComputedStyle(document.querySelector('html'))['font-size']
      ) >
    1024
  )
}

const USERSTATUS = {
  EXPIRED: 'expired',
  UNRECOGNIZED: 'unrecognized',
  ACTIVE: 'active'
}

const isDomainAllowed = () => {
  const host = window.location.hostname
  if (
    /localhost/.test(host) ||
    /qa\.ameripriseadvisors\.com/.test(host) ||
    /ameripriseadvisors\.com/.test(host)
  ) {
    return false
  } else {
    return true
  }
}

/*
 * Ameriprise Site Header (Utility Nav)
 */
class AmeripriseHeader extends HTMLElement {
  connectedCallback() {
    let current = this
    this.shadow = this.attachShadow({ mode: 'open' })
    let currentShadow = this.shadow

    this.template = this.querySelector('#header-template')

    if (this.template && this.template.content) {
      this.shadow.appendChild(this.template.content)

      /*
       * LoadTemplateBasedOnUserStatus
       * This method loads the personalized header for the user
       */
      const LoadTemplateBasedOnUserStatus = userStatus => {
        switch (userStatus) {
        case USERSTATUS.ACTIVE:
          current.userTemplate = current.querySelector(
            '#header-active-template'
          )
          break
        case USERSTATUS.EXPIRED:
          current.userTemplate = current.querySelector(
            '#header-expired-template'
          )
          break
        default:
          current.userTemplate = current.querySelector(
            '#header-prospect-template'
          )
          break
        }

        if (userStatus === USERSTATUS.ACTIVE) {
          current.init()
        }

        if (
          userStatus === USERSTATUS.UNRECOGNIZED &&
          (isDomainAllowed() || window.location.hostname === 'localhost')
        ) {
          this.initPersonalizedProspect(current, currentShadow)
        }
        this.setupHamburgerClick(current, currentShadow)

        // SiteNav Header Analytics
        const siteNavHeaderLinks = current.utilityNav.getElementsByTagName('a')
        siteNavHeaderLinks &&
          siteNavHeaderLinks.length > 0 &&
          siteNavHeaderLinks.forEach(link => {
            link &&
              link.addEventListener('click', event => {
                const message = event.currentTarget.text
                const type = 'header'
                const headerClass = event.currentTarget.classList

                if (headerClass.contains('analytics-FindAnAdvisor')) {
                  try {
                    const customAnalytics = new Analytics()
                    customAnalytics.setSatelliteTrack('initiativeFindAnAdvisor')
                  } catch (e) {}
                }

                if (!headerClass.contains('amp-clientActiveProfile')) {
                  try {
                    const analytics = new Analytics()
                    analytics.ping(message, type)
                  } catch (e) {}
                }
              })
          })
      }

      /*
       * Do not load the personalized header for the following sites:
       * AmeripriseAdvisors
       * NewsRoom
       * Localhost
       */
      const isSeriveCallAllowed = isDomainAllowed()
      if (isSeriveCallAllowed) {
        /*
         * GetUserStatus determine the status of the current user
         */
        const callValid =
          window.authState &&
          typeof window.authState.getUserStatus === 'function'

        callValid &&
          window.authState
            .getUserStatus()
            .then(currentAuthStatus => {
              if (currentAuthStatus) {
                LoadTemplateBasedOnUserStatus(currentAuthStatus)
              }
            })
            .catch(() => {
              LoadTemplateBasedOnUserStatus(USERSTATUS.UNRECOGNIZED)
            })
        if (!callValid) LoadTemplateBasedOnUserStatus(USERSTATUS.UNRECOGNIZED)
      } else {
        LoadTemplateBasedOnUserStatus(USERSTATUS.UNRECOGNIZED)
      }
    }
    return true
  }

  hideWelcome() {
    const clientWelcome = this.shadow.querySelector('.amp-clientWelcome')
    clientWelcome.classList.add('u-hidden')
  }

  setupHamburgerClick(current, currentShadow) {
    current.utilityNav = currentShadow.querySelector('.amp-utilityNav')
    current.utilityNav.appendChild(current.userTemplate.content)

    const hamburgerMenu = currentShadow.querySelector('.amp-hamburger')
    hamburgerMenu.addEventListener('click', event => {
      event.preventDefault()
      event.stopPropagation()
      window.dispatchEvent(
        new CustomEvent('menuclick', {
          detail: {
            currentTarget: event.currentTarget
          }
        })
      )
    })
  }

  async init() {
    await this.initClient()
  }

  async initClient() {
    // ignore advisors, newsroom
    const isSeriveCallAllowed = isDomainAllowed()
    if (!isSeriveCallAllowed) {
      this.hideWelcome()
      return
    }

    const { data, errors } = await getUser()

    if (errors && errors.length > 0) {
      this.hideWelcome()
    } else if (data && data.client && data.client.user) {
      const clientUser = data.client.user

      if (clientUser && clientUser.clFirstNm) {
        const clientActiveUserField = this.utilityNav.querySelector(
          '.amp-clientActiveProfile'
        )

        if (clientActiveUserField) {
          clientActiveUserField.innerHTML = xss(clientUser.clFirstNm)
        }
      }
    } else {
      this.hideWelcome()
    }

    return true
  }

  async initPersonalizedProspect(current, currentShadow) {
    const visitorInfo = await getVisitorInfo()
    let data =
      visitorInfo?.visitor?.targetingData?.advisorcollector?.extraData
        ?.attributes ?? {}

    if (data !== undefined && Object.keys(data).length > 0) {
      current.utilityNav = currentShadow.querySelector('.amp-utilityNav')
      current.utilityNav.appendChild(current.userTemplate.content)

      const navElt = document.createElement('nav')
      navElt.id = 'personalized-sticky-advisor'
      navElt.classList.add('amp-nav-level-one')
      let ulElt = document.createElement('ul')
      ulElt.setAttribute('role', 'menubar')

      navElt.appendChild(ulElt)
      const liElt = document.createElement('li')
      liElt.classList.add(
        'List-item',
        'u-margin0',
        'u-paddingTop0',
        'u-paddingBottom0'
      )
      liElt.setAttribute('role', 'menuitem')
      ulElt.appendChild(liElt)

      const personalizedButton = PersonalizedButton(data)
      liElt.appendChild(personalizedButton)

      let findAdvisorButton = currentShadow.querySelector(
        'a[data-qa="FindAdvisorButton"]'
      )
      if (findAdvisorButton) findAdvisorButton.replaceWith(navElt)
      let stickyBar = new Menubar(navElt.parentNode) // keyboard navigation
      stickyBar.init(
        document.querySelector('ameriprise-header'),
        'div li',
        'div li a'
      )
      stickyBar.setMode(IsLargeViewport())
    }

    return true
  }
}

if (!window.customElements.get('ameriprise-header')) {
  window.customElements.define('ameriprise-header', AmeripriseHeader)
}
